import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { assign, keys, pick } from 'lodash-es';
import { take } from 'rxjs/operators';
import {
  TimeBlockAbsenceModel,
  TimeBlockAppointmentModel,
  TimeBlockModel,
  TimeBlockProjectModel,
} from '../../../../../core/models/timeblock/time-block.model';
import { selectAbsenceReason } from '../../../../../core/state/settings/absence-reasons/absence-reasons.selectors';
import { DropdownOption } from '../../../../../shared/data-types/dropdown-option';
import { dropdownOptionToEntity } from '../../../../../shared/functions/dropdown-functions';
import { DateTimeHelper } from '../../../util/date-time-helper';
import { TimeBlockComponent } from '../time-block.component';
import { ITimeBlockComponentItem } from '../types/time-block-component-item-types';
import { TimeBlockContentType } from '../types/time-block-types';

@Injectable()
export class TimeBlockHydrationService {
  private readonly store = inject(Store);

  public hydrateTimeBlockModel(
    timeBlockItem: ITimeBlockComponentItem,
    controls,
    isFixInterval: boolean,
  ): TimeBlockModel {
    const timeBlockContentType = timeBlockItem.timeBlockContentType;
    let updatedTimeBlockModel: any;

    if (timeBlockContentType === TimeBlockContentType.Project) {
      updatedTimeBlockModel = this.updateProjectTypeTimeBlock(timeBlockItem, controls);
    } else if (timeBlockContentType === TimeBlockContentType.Absence) {
      updatedTimeBlockModel = this.updateAbsenceTypeTimeBlock(timeBlockItem, controls);
    } else if (timeBlockContentType === TimeBlockContentType.Appointment) {
      updatedTimeBlockModel = new TimeBlockAppointmentModel();
      Object.assign(updatedTimeBlockModel, timeBlockItem.timeBlockModel);
    } else {
      throw new Error('Content type not supported.');
    }

    let startDate: Date, endDate: Date;
    if (isFixInterval) {
      const timeOfDay = controls.timeOfDay;
      updatedTimeBlockModel.fixInterval = timeOfDay.value.value; // This is a dropdown value, therefore value.value.
      startDate = DateTimeHelper.startOfDay(controls.dateFrom.value);
      endDate = DateTimeHelper.startOfDay(controls.dateTo.value);
    } else {
      updatedTimeBlockModel.fixInterval = null;
      startDate = DateTimeHelper.mergeDateAndTime(controls.dateFrom.value, controls.timeFrom.value);
      endDate = DateTimeHelper.mergeDateAndTime(controls.dateTo.value, controls.timeTo.value);
    }

    updatedTimeBlockModel.description = controls.description.value ?? '';
    updatedTimeBlockModel.start = startDate;
    updatedTimeBlockModel.end = endDate;
    return updatedTimeBlockModel;
  }

  private updateProjectTypeTimeBlock(
    timeBlock: ITimeBlockComponentItem,
    controls,
  ): TimeBlockProjectModel {
    const updatedTimeBlockModel = new TimeBlockProjectModel();
    assign(updatedTimeBlockModel, pick(timeBlock.timeBlockModel, keys(updatedTimeBlockModel)));

    // After changing the task in the Shive Tracker, update the project details here.
    updatedTimeBlockModel.task = controls.task.value?.value
      ? dropdownOptionToEntity(controls.task.value as DropdownOption)
      : null;
    return updatedTimeBlockModel;
  }

  private updateAbsenceTypeTimeBlock(
    timeBlockItem: ITimeBlockComponentItem,
    controls,
  ): TimeBlockAbsenceModel<TimeBlockComponent> {
    const updatedTimeBlockModel = new TimeBlockAbsenceModel<TimeBlockComponent>();
    assign(updatedTimeBlockModel, pick(timeBlockItem.timeBlockModel, keys(updatedTimeBlockModel)));

    const selectedDropdownReason = dropdownOptionToEntity(controls.reason.value as DropdownOption);

    this.store
      .select(selectAbsenceReason(+selectedDropdownReason.id))
      .pipe(take(1))
      .subscribe((absenceReason) => (updatedTimeBlockModel.reason = absenceReason));

    return updatedTimeBlockModel;
  }
}
